import { IndustryId, Organization, OrgId, SectorId } from "./organization";

export interface OrgFilter {
  // Sectors and industries are mutually exclusive.
  sector: SectorId[];
  industry: IndustryId[];

  // If this is set then all the other fields are ignored, and only organizations
  // with these specific IDs are accepted.
  search: OrgId[];

  // TODO: add headquaters filter to this structure, too.
}

export type OrgFilterProp = keyof OrgFilter;

const EXCLUSIVE_PROPS: { [k in OrgFilterProp]: OrgFilterProp[] } = {
  industry: ["sector", "search"],
  sector: ["industry", "search"],
  search: ["industry", "sector"],
};

export const empty: OrgFilter = {
  sector: [],
  industry: [],
  search: [],
};

export const hasValue = (fstate: OrgFilter, prop: OrgFilterProp, val: string): boolean =>
  fstate[prop].indexOf(val) !== -1;

export const normalize = (fstate: OrgFilter, prop: OrgFilterProp): OrgFilter => {
  const cs = EXCLUSIVE_PROPS[prop];
  if (cs != null) {
    return cs.reduce((state, c) => ({ ...state, [c]: [] }), fstate);
  } else {
    return fstate;
  }
};

export const removeValue = (fstate: OrgFilter, prop: OrgFilterProp, val: string): OrgFilter => ({
  ...fstate,
  [prop]: fstate[prop].filter((x) => x !== val),
});

export const insertValue = (fstate: OrgFilter, prop: OrgFilterProp, val: string): OrgFilter => {
  if (fstate[prop].indexOf(val) !== -1) {
    return fstate;
  } else {
    return normalize({ ...fstate, [prop]: [...fstate[prop], val] }, prop);
  }
};

export const toggle = (fstate: OrgFilter, prop: OrgFilterProp, val: string): OrgFilter => {
  if (hasValue(fstate, prop, val)) {
    return removeValue(fstate, prop, val);
  } else {
    return insertValue(fstate, prop, val);
  }
};

export const set = (fstate: OrgFilter, prop: OrgFilterProp, val: string): OrgFilter => ({
  ...fstate,
  [prop]: [val],
});

export const acceptsValue = (fstate: OrgFilter, prop: OrgFilterProp, val: string) => {
  const vals = fstate[prop];
  return vals.length === 0 || vals.indexOf(val) !== -1;
};

export const acceptsOrg = (fstate: OrgFilter, org: Organization): boolean =>
  acceptsValue(fstate, "sector", org.sector_object.key) &&
  acceptsValue(fstate, "industry", org.industry_object.key) &&
  acceptsValue(fstate, "search", org.id);

export const filterOrgs = (fstate: OrgFilter, organizations: Organization[]): Organization[] =>
  organizations.filter((org) => acceptsOrg(fstate, org));

export const clear = (fstate: OrgFilter, prop: OrgFilterProp): OrgFilter => ({
  ...fstate,
  [prop]: [],
});

export const equals = (fstate1: OrgFilter, fstate2: OrgFilter): boolean =>
  JSON.stringify(fstate1) === JSON.stringify(fstate2);

export const changed = (fstate1: OrgFilter, fstate2: OrgFilter): boolean => !equals(fstate1, fstate2);

// export const asArray = (fstate: OrgFilter, prop: OrgFilterProp): string[] =>
//   [...fstate[prop]]

export const asObject = (fstate: OrgFilter): OrgFilter => ({ ...fstate });

export const fromObject = (obj: any): OrgFilter => {
  let fstate = empty;
  for (const prop in obj) {
    const vals = obj[prop];
    for (let i = 0, len = vals.length; i < len; i++) {
      const val = vals[i];
      fstate = insertValue(fstate, prop as any, val);
    }
  }
  return fstate;
};
